import type { EmailContext } from "../types";

function extractRecipients(
  recipients: Office.EmailAddressDetails[] | undefined
): Array<{ displayName: string; emailAddress: string }> {
  if (!recipients) return [];
  return recipients.map((r) => ({
    displayName: r.displayName,
    emailAddress: r.emailAddress,
  }));
}

function getBodyAsync(
  body: Office.Body,
  type: Office.CoercionType
): Promise<string> {
  return new Promise((resolve, reject) => {
    body.getAsync(type, (result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        resolve(result.value);
      } else {
        reject(new Error(result.error?.message ?? "Failed to read body"));
      }
    });
  });
}

function getComposeSubject(subject: Office.Subject): Promise<string> {
  return new Promise((resolve, reject) => {
    subject.getAsync((result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        resolve(result.value);
      } else {
        reject(new Error(result.error?.message ?? "Failed to read subject"));
      }
    });
  });
}

function getComposeRecipients(
  recipients: Office.Recipients
): Promise<Array<{ displayName: string; emailAddress: string }>> {
  return new Promise((resolve, reject) => {
    recipients.getAsync((result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        resolve(extractRecipients(result.value));
      } else {
        reject(
          new Error(result.error?.message ?? "Failed to read recipients")
        );
      }
    });
  });
}

export async function readEmailContext(): Promise<EmailContext> {
  // Browser testing fallback — no Outlook context available
  if (!Office.context?.mailbox?.item) {
    return {
      itemId: "browser-test",
      conversationId: "browser-test",
      subject: "Test: Anfrage zu Liefertermin",
      sender: { displayName: "Max Mustermann", emailAddress: "max.mustermann@example.de" },
      recipients: [{ displayName: "Kundenservice", emailAddress: "service@logicline.eu" }],
      cc: [],
      bodyHtml: "",
      bodyText: "Sehr geehrte Damen und Herren,\n\nich habe am 10.03. eine Bestellung aufgegeben (Bestellnr. 12345). Können Sie mir mitteilen, wann die Lieferung voraussichtlich eintrifft?\n\nMit freundlichen Grüßen\nMax Mustermann",
      dateTimeCreated: new Date(),
      mode: "read",
    };
  }

  const item = Office.context.mailbox.item;

  const isRead = item.itemType === Office.MailboxEnums.ItemType.Message
    && !("subject" in item && typeof (item as any).subject?.getAsync === "function");

  // Detect compose mode by checking if subject is a Subject object (has getAsync)
  const isCompose = !isRead
    && "subject" in item
    && typeof (item as any).subject?.getAsync === "function";

  const mode: "read" | "compose" = isCompose ? "compose" : "read";

  let subject: string;
  let recipients: Array<{ displayName: string; emailAddress: string }>;
  let cc: Array<{ displayName: string; emailAddress: string }>;
  let sender: { displayName: string; emailAddress: string };
  let dateTimeCreated: Date | null;

  if (mode === "compose") {
    const composeItem = item as Office.MessageCompose;
    subject = await getComposeSubject(composeItem.subject);
    recipients = await getComposeRecipients(composeItem.to);
    cc = await getComposeRecipients(composeItem.cc);
    sender = {
      displayName: Office.context.mailbox.userProfile.displayName,
      emailAddress: Office.context.mailbox.userProfile.emailAddress,
    };
    dateTimeCreated = null;
  } else {
    const readItem = item as Office.MessageRead;
    subject = readItem.subject;
    recipients = extractRecipients(readItem.to);
    cc = extractRecipients(readItem.cc);
    sender = readItem.from
      ? {
          displayName: readItem.from.displayName,
          emailAddress: readItem.from.emailAddress,
        }
      : { displayName: "", emailAddress: "" };
    dateTimeCreated = readItem.dateTimeCreated;
  }

  const [bodyHtml, bodyText] = await Promise.all([
    getBodyAsync(item.body, Office.CoercionType.Html),
    getBodyAsync(item.body, Office.CoercionType.Text),
  ]);

  return {
    itemId: item.itemId ?? "",
    conversationId: item.conversationId ?? "",
    subject,
    sender,
    recipients,
    cc,
    bodyHtml,
    bodyText,
    dateTimeCreated,
    mode,
  };
}
